import { Grid, Stack, Typography } from "@mui/material";
import { useApp } from "../hooks/useApp";
import React, { useState, forwardRef } from "react";
import "./Factorymap.css";
import SvgIcon from "@mui/material/SvgIcon";
import { ReactComponent as SvgTulip } from "../svgs/tulipwhite.svg";
import { ReactComponent as TulipLibrary } from "../svgs/tulipLibraryWhite.svg";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import FactoryAndPanel from "../pages/composable/FactoryAndPanel";
import Process from "./Process";
import LogoMaster from "./LogoMaster";
import { useTheme } from "@mui/material/styles";
import { PoweredByLogo } from "./Common/PoweredByLogo";
import Gobutton from "./Gobutton";
import UnsSidebar from "../pages/uns/UnsSidebar";

const FactoryContent = forwardRef(function FactoryContent({
  activeFactory,
  isModal,
  isFactoryDirect = false,
}, ref) {
  const theme = useTheme();

  const style = {
    p: 0,
    width: "100%",
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    backgroundColor: "#FFF",
  };

  const { setActiveFactory, selectNode, aiFeatures, combinedAssets, svgLogo } = useApp();

  const handleClose = () => {
    setActiveFactory(null);
    selectNode(null);
  };

  const factoryData = () => {
    if (activeFactory.Suites) {
      return true;
    } else {
      // Ensure Children exists before mapping
      const children = activeFactory.Children || [];
      
      let outputData = [
        {
          ...activeFactory,
          color: theme.palette.info.main,
          Children: children.map((child) => {
            // Determine if child is a string (ID) or an object with an id key
            const childId = typeof child === "string" ? child : child.id;
            // Find the matching asset from combinedAssets
            let matchedAsset = combinedAssets.find(
              (vtecAsset) => vtecAsset.id === childId
            );
            if (matchedAsset) {
              // If child is an object, replace Top and Left if they exist
              if (typeof child === "object") {
                matchedAsset = {
                  ...matchedAsset,
                  Top: child.Top !== undefined ? child.Top : matchedAsset.Top,
                  Left:
                    child.Left !== undefined ? child.Left : matchedAsset.Left,
                  flow: child?.flow ? child.flow : matchedAsset.flow,
                  color: child?.color ? child.color : "#4E79A7"
                };
              }
            }
            return matchedAsset;
          }).filter((child) => child), // Remove any undefined values
          AiFeatures: activeFactory ? activeFactory?.AiFeatures?.map((child) => {
            // Determine if child is a string (ID) or an object with an id key
            const childId = typeof child === "string" ? child : child.id;
            // Find the matching asset from combinedAssets
            let matchedAsset = aiFeatures.find(
              (vtecAsset) => vtecAsset.id === childId
            );
            if (matchedAsset) {
              // If child is an object, replace Top and Left if they exist
              if (typeof child === "object") {
                matchedAsset = {
                  ...matchedAsset,
                  Top: child.Top !== undefined ? child.Top : matchedAsset.Top,
                  Left:
                    child.Left !== undefined ? child.Left : matchedAsset.Left,
                  flow: child?.flow ? child.flow : matchedAsset.flow,
                  color: child?.color ? child.color : "#4E79A7"
                };
              }
            }
            return matchedAsset;
          }).filter((child) => child) : []
        },
      ];
      return outputData;
    }
  };
  const data = factoryData();

  const [selectedSuite] = useState(data[0]);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const beaconData = selectedSuite?.Children?.sort(
    (a, b) =>
      selectedSuite.Children.indexOf(a.id) -
      selectedSuite.Children.indexOf(b.id)
  ) || [];

  const hasFlowTrue = () => {
    return beaconData.some((obj) => obj.flow === true);
  };

  const showUnsSidebar = !!activeFactory.unsConfig?.enabled;

  const innerContent = (
    <Stack
      ref={ref}
      tabIndex={-1}
      className={showUnsSidebar ? "" : "main"}
      direction={"column"}
      sx={{
        ...style,
        width: "100%",
        height: "100%",
        overflow: "hidden",
        borderRadius: 0,
        border: "0px",
        backgroundColor: "",
        outline: "none",
      }}
    >
      <Grid container padding={2} alignItems={"center"}>
        <Grid item xs={12} sm={4} className="h-[45px]">
          <SvgIcon
            inheritViewBox
            sx={{ height: "45px", width: "auto", color: "customColors.white" }}
            component={svgLogo ? LogoMaster : SvgTulip}
          />
        </Grid>
        <Grid item xs={12} sm={4} textAlign={"center"}>
          <Typography variant="h5" color={"customColors.white"}>
            {activeFactory.Title}
          </Typography>
        </Grid>
        <Grid item xs={12} sm={4} textAlign={"right"}>
          {isModal === true && (
            <IconButton
              onClick={handleClose}
              size="large"
              sx={{ color: "customColors.white" }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          )}
        </Grid>
      </Grid>
      {hasFlowTrue() && (
        <Process
          beaconData={beaconData}
          selectedAsset={selectedAsset}
          setSelectedAsset={setSelectedAsset}
        />
      )}
      <FactoryAndPanel
        selectedAsset={selectedAsset}
        setSelectedAsset={setSelectedAsset}
        selectedSuite={selectedSuite}
        hasSidebar={showUnsSidebar}
      />

      {activeFactory.tulipLibrary && (
        <div className="flex flex-row justify-end gap-2 m-5">
          <Gobutton
            text={"Open in Tulip Library"}
            startIcon={
              <SvgIcon
                className="tulipLibrary"
                inheritViewBox
                sx={{
                  height: "18px",
                  width: "auto",
                  color: "customColors.white",
                }}
                component={TulipLibrary}
              />
            }
            url={{ type: "beacon", url: activeFactory.tulipLibrary }}
          />
        </div>
      )}

      {isFactoryDirect && (
        <PoweredByLogo
          style={{
            position: "absolute",
            bottom: "10px",
            right: "20px",
          }}
        />
      )}
    </Stack>
  );

  if (showUnsSidebar) {
    return (
      <div
        className="main standardGradient"
        style={{
          display: "flex",
          flexDirection: "row",
          width: "100%",
          height: "100%",
          overflow: "hidden",
        }}
      >
        <UnsSidebar
          mqttConfigOverride={activeFactory.unsConfig.mqttConfig}
          siteFilters={activeFactory.unsConfig.siteFilters}
          topicBranches={activeFactory.unsConfig.topicBranches}
          defaultFilter={activeFactory.unsConfig.defaultFilter}
        />
        <div style={{ flex: 1, overflow: "hidden", position: "relative" }}>
          {innerContent}
        </div>
      </div>
    );
  }

  return innerContent;
});

export default FactoryContent;
